'use client'

/**
 * @file use-nav-signals.ts
 * @description Gathers the {@link NavSignals} every nav surface (sidebar, mobile
 * nav sheet, Cmd+K palette) hands to the resolver in `./resolve.ts`.
 *
 * The ONLY file that reads PostHog for nav. Nav visibility is presentation, not
 * authorization: route guards live in the layouts and the backend.
 *
 * Works without a `SchoolProvider` (the marketing MainNav renders MobileNav
 * without one): the viewer then holds no permissions and has no active school.
 */

import { useMemo } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react'

import { useSchoolOptional } from '@/components/context/school-context'
import { buildViewerAccess } from '@/lib/access/viewer-access'
import { useArbiterConfigured } from '@/lib/hooks/use-arbiter'
import { useMounted } from '@/lib/hooks/use-mounted'
import { useScreenFeature } from '@/lib/screen-features'
import type { NavFlagKey, NavSignals } from './types'

const NO_ACCESS = buildViewerAccess({ isSuperAdmin: false, schoolPermissions: [] })

export function useNavSignals(): { signals: NavSignals; ready: boolean } {
  const school = useSchoolOptional()
  const { isLoaded } = useAuth()
  const mounted = useMounted()

  // Super-admin passes from the claim even while school permissions are still loading.
  const viewer = school?.viewerAccess ?? NO_ACCESS
  const hasActiveSchool = !!school?.currentSchool
  // The one place `!== false` is right: an unknown setup state must not hide the whole top nav.
  const isSchoolSetupComplete = school?.currentSchool?.isSetupComplete !== false

  // No school id disables the query, so `data` stays undefined and this is false.
  const { data: arbiterConfigured } = useArbiterConfigured(school?.currentSchool?.id)
  const isArbiterConfigured = arbiterConfigured?.configured === true

  // The school's own `call` block, the same cached read the Calling panel saves
  // through, so turning the module on shows the Calls entry without a reload.
  // Loading, errored and absent all read as off.
  const { value: callConfig } = useScreenFeature('call', school?.currentSchool?.id)
  const isCallingOn = callConfig?.enabled === true

  // One reactive hook call per flag (rules-of-hooks: no loop). Reactive on
  // purpose: flags that load after mount, or reload after a school switch, must
  // re-render every surface. A memoised closure over `posthog.isFeatureEnabled`
  // would keep the pre-load answer.
  //
  // Until PostHog's /flags response lands, `useFeatureFlagEnabled` returns the
  // provider's bootstrap value, which holds marketing flags only, so every nav
  // flag would read undefined. Fall back to PostHog's persisted cache (what the
  // surfaces read before) so a returning user's flagged items don't pop in late.
  const posthog = usePostHog()
  const flagOn = (value: boolean | undefined, flag: NavFlagKey) => (value ?? posthog?.isFeatureEnabled(flag)) === true
  const schoolAdminManagePages = flagOn(useFeatureFlagEnabled('school_admin_manage_pages'), 'school_admin_manage_pages')
  const schoolAdminAdmissions = flagOn(useFeatureFlagEnabled('school_admin_admissions'), 'school_admin_admissions')
  const athleticsHubWeb = flagOn(useFeatureFlagEnabled('athletics_hub_web'), 'athletics_hub_web')
  const docsNavigation = flagOn(useFeatureFlagEnabled('docs_navigation'), 'docs_navigation')

  const signals = useMemo<NavSignals>(() => {
    // A `Record` over every key, so a new `NAV_FLAG_KEYS` entry fails tsc until it is wired above.
    const flags: Record<NavFlagKey, boolean> = {
      school_admin_manage_pages: schoolAdminManagePages,
      school_admin_admissions: schoolAdminAdmissions,
      athletics_hub_web: athleticsHubWeb,
      docs_navigation: docsNavigation
    }
    return {
      viewer,
      hasActiveSchool,
      isSchoolSetupComplete,
      isArbiterConfigured,
      isCallingOn,
      isFlagEnabled: (flag: NavFlagKey) => flags[flag] === true
    }
  }, [
    viewer,
    hasActiveSchool,
    isSchoolSetupComplete,
    isArbiterConfigured,
    isCallingOn,
    schoolAdminManagePages,
    schoolAdminAdmissions,
    athleticsHubWeb,
    docsNavigation
  ])

  return { signals, ready: mounted && isLoaded }
}
